import React from 'react';
import {type Epic, ofType} from 'redux-observable';
import {Observable, merge, of} from 'rxjs';
import {mergeMap, catchError, map, flatMap, tap, take} from 'rxjs/operators';
import moment from 'moment-timezone';
import i18next from 'i18next';
import {List} from 'immutable';
import {getFormValues, getFormInitialValues} from 'redux-form';

import {combineEpics} from 'web-app/util/redux-observable';
import TranslatedMarkdown from 'web-app/react/components/markdown/translated-markdown';
import Markdown from 'web-app/react/components/markdown/markdown';
import {showDialogObservable, showAlertObservable, DialogChoice} from 'web-app/react/dialog';
import * as BillPayerActions from 'web-app/react/entities/bill-payers/actions';
import * as AccountItemActions from 'web-app/react/entities/account-items/actions';
import * as ManagePlanActions from 'web-app/react/plans/manage-plan/actions';
import * as PurchaseModalActions from 'web-app/react/modals-container/purchase-modal/actions';
import * as InvoiceModalActions from 'web-app/react/modals-container/invoice/actions';
import * as ChildActions from 'web-app/react/entities/children/actions';
import {replayOnTriggerObservable} from 'web-app/util/epics-helpers';
import * as AccountItemApi from 'web-app/react/entities/account-items/api';
import {InvoiceMode} from 'web-app/util/graphql/api-types';
import InvoiceBehaviors from 'web-app/behaviors/invoice-behavior-ids';
import * as DepositActions from 'web-app/react/entities/deposits/actions';
import * as Famlytics from 'web-app/util/famlytics';
import {TrackingEvents} from 'web-app/util/famlytics/events';
import * as InvoicePeriodPickerConstants from 'web-app/react/components/form-elements/invoice-period-picker/constants';
import {type RootState} from 'web-app/react/redux/main-reducer';
import * as IntercomEventTracking from 'web-app/util/intercom/intercom-event-tracking';

import * as Actions from './actions';
import {type DateTimePeriod} from './types';
import * as Api from './api';
import * as Constants from './constants';
import * as Selectors from './selectors';

const convertFormDataToGraphQlInvoiceVariables = (
    invoiceData: DateTimePeriod,
    billPayerIds: List<string> | Array<string>,
    preview: boolean,
) => {
    const billPayerIdsArray = List.isList(billPayerIds) ? billPayerIds.toArray() : billPayerIds;

    return {
        requests: billPayerIdsArray.map(billPayerId => ({
            billPayerId,
        })),
        range: {
            from: invoiceData.from,
            to: invoiceData.to,
        },
        mode: preview ? InvoiceMode.Preview : InvoiceMode.BatchPersist,
        invoiceDate: invoiceData.invoiceDate,
        dueDate: invoiceData.due,
        notify: invoiceData.notify,
    };
};

/**
 * Fetches all data necessary for showing batch invoicing for a given invoice period
 * @param action$
 */
const fetchBatchInvoiceData = action$ =>
    action$.ofType(Actions.fetchAllData.type).pipe(
        mergeMap(({payload}) => {
            const {invoicePeriod, institutionId, isBatchInvoicingLimited} = payload;

            const validationError = getValidationError(invoicePeriod, 'YYYY-MM-DD');

            if (validationError) {
                return showAlertObservable({title: '', description: validationError}).pipe(
                    map(() => Actions.fetchAllDataFailed.action({message: validationError})),
                );
            }

            return Api.fetchInvoicingBillPayers(institutionId, invoicePeriod.from, invoicePeriod.to).pipe(
                mergeMap((data: any) => {
                    const billPayerIds = data.billPayers.map(bp => bp.billPayerId);

                    const useGraphQl = data.behaviors.includes(InvoiceBehaviors.UseGraphQLInvoicing);

                    const invoicingBillPayerResultsObservable = Observable.of(
                        BillPayerActions.fetchAllSuccess.action({billPayers: data.billPayers}),
                        ChildActions.fetchAllSuccess.action(data.children),
                        Actions.fetchInvoicingBillPayersSuccess.action({
                            billPayerIds,
                            useGraphQl,
                            warnings: data.warnings,
                            invoices: data.invoices,
                            behaviors: data.behaviors,
                        }),
                    );

                    const invoiceVariables = useGraphQl
                        ? convertFormDataToGraphQlInvoiceVariables(invoicePeriod, billPayerIds, true)
                        : null;

                    const previewBatchInvoicingObservable = useGraphQl
                        ? AccountItemApi.previewBatchInvoice(invoiceVariables).map(data => {
                              return Actions.previewBatchInvoiceSuccess.action({
                                  invoicePreviews: data.invoices
                                      .groupBy(invoice => invoice.billPayerId)
                                      .map(invoices => {
                                          const firstEntry = invoices.first();
                                          return invoices.count() === 1 && firstEntry && firstEntry.type === 'error'
                                              ? {
                                                    error: firstEntry.error,
                                                    errorCode: firstEntry.errorCode,
                                                    invoices: List(),
                                                }
                                              : {invoices};
                                      })
                                      .toMap(),
                                  isBatchInvoicingLimited,
                              });
                          })
                        : Api.previewBatchInvoice(institutionId, billPayerIds, invoicePeriod).pipe(
                              map(invoicePreviews =>
                                  Actions.previewBatchInvoiceSuccess.action({invoicePreviews, isBatchInvoicingLimited}),
                              ),
                              catchError(error => Observable.of(Actions.previewBatchInvoiceFailed.action(error))),
                          );

                    return Observable.merge(invoicingBillPayerResultsObservable, previewBatchInvoicingObservable);
                }),
                catchError(error => Observable.of(Actions.fetchAllDataFailed.action(error))),
            );
        }),
    );

const getValidationError = (values: any, format = 'YYYY-MM-DD') => {
    if (values.from && values.to) {
        const from = moment(values.from, format);
        const to = moment(values.to, format);
        if (from.isAfter(to)) {
            return i18next.t('plans.debt.fromBeforeTo');
        }
        return null;
    }
    return i18next.t('plans.debt.fromToMustBeSat');
};

/**
 * Batch invoice all billPayerIds in the payload of the action
 * Will reload all batch invoicing data on success
 * @param action$
 */
const batchInvoice: Epic<any, any, RootState> = (action$, state$) =>
    action$.ofType(Actions.batchInvoice.type).mergeMap(({payload}) => {
        const {invoiceData, confirmText, useGraphQl, billPayerIds} = payload;

        const validationError = getValidationError(invoiceData);

        if (validationError) {
            return showAlertObservable({title: '', description: validationError}).map(() =>
                Actions.batchInvoiceFailed.action({message: validationError}),
            );
        } else {
            return showDialogObservable({
                title: '',
                description: confirmText
                    ? React.createElement(Markdown, {source: confirmText})
                    : React.createElement(TranslatedMarkdown, {translationKey: 'plans.debt.batchInvoiceConfirmShort'}),
            }).pipe(
                mergeMap(selection => {
                    if (selection === DialogChoice.OK) {
                        const timestampBeforeStartingBatchInvoiceRun = Date.now();

                        const trackingProperties = getBatchInvoiceTrackingProperties(state$.value, useGraphQl);

                        trackBatchInvoicing({
                            event: {name: TrackingEvents.BATCH_INVOICE_CREATE_AND_SEND_INVOICES_CONFIRM.name},
                            properties: trackingProperties,
                            type: 'famlytics',
                        });

                        const invoiceVariables = useGraphQl
                            ? convertFormDataToGraphQlInvoiceVariables(invoiceData, billPayerIds, false)
                            : null;

                        const createObservable = useGraphQl
                            ? AccountItemApi.persistBatchInvoiceWithoutPDF(invoiceVariables)
                            : Api.batchInvoice(payload.institutionId, payload.billPayerIds, invoiceData).map(
                                  response => {
                                      return {
                                          invoices: response.invoices,
                                          runIds: response.runId ? [response.runId] : [],
                                      };
                                  },
                              );

                        return createObservable.pipe(
                            flatMap(({runIds}) =>
                                merge(
                                    // Fetch batch invoice data all over
                                    of(Actions.batchInvoiceSuccess.action(runIds)),

                                    // Track that we reached the completion of the batch invoice run.
                                    action$.pipe(
                                        ofType(Actions.batchInvoicingRunCompleted.type),

                                        // Only take the first event!
                                        // Otherwise, we'll register too many completed events within
                                        // the same session - as this observable is recreated each time
                                        // we start a batch invoice run.
                                        take(1),
                                        tap(() => {
                                            const loadingTime = Date.now() - timestampBeforeStartingBatchInvoiceRun;

                                            trackBatchInvoicing({
                                                event: {
                                                    name: TrackingEvents.BATCH_INVOICE_CREATE_AND_SEND_INVOICES_COMPLETE
                                                        .name,
                                                    loadingTime,
                                                },
                                                properties: trackingProperties,
                                                type: 'famlytics',
                                            });

                                            trackBatchInvoicing({
                                                event: IntercomEventTracking.EventName.BATCH_INVOICES_SENT_OUT,
                                                type: 'intercom',
                                            });
                                        }),
                                    ),
                                ),
                            ),
                            catchError(e => Observable.of(Actions.batchInvoiceFailed.action(e))),
                        );
                    } else {
                        return Observable.of(Actions.batchInvoiceAborted.action());
                    }
                }),
            );
        }
    });

const listenForUpdates = (action$, state$) => {
    const replayObservable = action$.ofType(Actions.fetchAllData.type);
    const resetObservable = action$.ofType(Actions.showingInstitutionId.type);

    const triggerReplayObservable = Observable.merge(
        action$.ofType(DepositActions.createSuccess.type),
        action$.ofType(DepositActions.deleteDepositSuccess.type),
        action$.ofType(DepositActions.returnDepositSuccess.type),
        action$.ofType(DepositActions.deleteDepositReturnSuccess.type),
        action$.ofType(ManagePlanActions.createPlanSuccess.type),
        action$.ofType(ManagePlanActions.updatePlanSuccess.type),
        action$.ofType(ManagePlanActions.deletePlanSuccess.type),
        action$.ofType(PurchaseModalActions.hidePurchaseModalAfterUpdate.type),
        action$.ofType(PurchaseModalActions.hidePurchaseModalAfterDelete.type),
        action$.ofType(Actions.batchInvoiceSuccess.type),
        action$.ofType(AccountItemActions.createPaymentSuccess.type),
        action$.ofType(AccountItemActions.updatePaymentSuccess.type),
        action$.ofType(AccountItemActions.deletePaymentSuccess.type),
        action$.ofType(AccountItemActions.createInvoiceSuccess.type),
        action$.ofType(AccountItemActions.createInvoiceWithGraphQLSuccess.type),
        action$.ofType(InvoiceModalActions.cancelSuccess.type),
        action$.ofType(BillPayerActions.updateSplitSuccess.type),
    );

    const predicate = replayAction =>
        state$.value.plans.batchInvoice.institutionId === replayAction.payload.institutionId;

    return replayOnTriggerObservable(replayObservable, resetObservable, triggerReplayObservable, predicate).catch(
        () => {
            return Observable.empty();
            // This is intentionally blank since the error handling is done in whatever handles the replay actions
        },
    );
};

const getBatchInvoiceTrackingProperties = (state: RootState, graphQl: boolean) => {
    const formValues: any = getFormValues(Constants.INVOICE_MONTH_SELECTOR_FORM_NAME)(state);
    const initialFormValues: any = getFormInitialValues(Constants.INVOICE_MONTH_SELECTOR_FORM_NAME)(state);

    const countInvoicesCreated = Selectors.getSelectedBillPayers(state)?.length ?? 0;

    const invoicePeriodMonthOrCustom = formValues?.periodType;

    const invoicePeriodMonthChosen =
        invoicePeriodMonthOrCustom === InvoicePeriodPickerConstants.FORM_PERIOD_TYPE_MONTH
            ? Number(moment(formValues?.invoiceMonth, 'YYYY-MM').format('MM'))
            : undefined;
    const invoiceCustomPeriodStartDate =
        invoicePeriodMonthOrCustom === InvoicePeriodPickerConstants.FORM_PERIOD_TYPE_CUSTOM
            ? formValues?.from
            : undefined;
    const invoiceCustomPeriodEndDate =
        invoicePeriodMonthOrCustom === InvoicePeriodPickerConstants.FORM_PERIOD_TYPE_CUSTOM
            ? formValues?.to
            : undefined;

    const invoicePeriodPastPresentOrFuture =
        formValues?.from && formValues?.to
            ? getTemporalPositionRelativeToInvoicePeriod(formValues.from, formValues.to)
            : undefined;

    const dueDate = formValues?.due;
    const dueDateEqualDefault = dueDate && initialFormValues ? dueDate === initialFormValues.due : undefined;

    const invoiceDate = formValues?.invoiceDate;
    const invoiceDateEqualDefault =
        invoiceDate && initialFormValues ? invoiceDate === initialFormValues.invoiceDate : undefined;

    const sendEmailsUnchecked = formValues ? !formValues.notify : undefined;

    const unresolvedIssues = state.plans.batchInvoice.warnings;
    const countUnresolvedIssuesIgnoredInBatch = state.plans.batchInvoice.warnings?.size;

    // Traverse and extract all unique issue types - and convert them to something human readable.
    const unresolvedIssuesTypes = unresolvedIssues
        .map(issue => issue.error)
        .toSet()
        .map(errorCode => mapIssueErrorCodeToTrackingCategory(errorCode))
        .toArray();

    return {
        graphQl,
        countInvoicesCreated,
        countUnresolvedIssuesIgnoredInBatch,
        unresolvedIssuesTypes,
        invoicePeriodPastPresentOrFuture,
        invoicePeriodMonthOrCustom,
        invoicePeriodMonthChosen,
        invoiceCustomPeriodStartDate,
        invoiceCustomPeriodEndDate,
        dueDateEqualDefault,
        dueDate,
        invoiceDateEqualDefault,
        invoiceDate,
        sendEmailsUnchecked,
    };
};

type TrackBatchInvoicingArgs =
    | {
          event:
              | {name: typeof TrackingEvents.BATCH_INVOICE_CREATE_AND_SEND_INVOICES_CONFIRM['name']}
              | {
                    name: typeof TrackingEvents.BATCH_INVOICE_CREATE_AND_SEND_INVOICES_COMPLETE['name'];
                    loadingTime: number;
                };
          properties: Record<string, unknown>;
          type: 'famlytics';
      }
    | {
          event: typeof IntercomEventTracking.EventName.BATCH_INVOICES_SENT_OUT;
          type: 'intercom';
      };

const trackBatchInvoicing = (args: TrackBatchInvoicingArgs) => {
    if (args.type === 'intercom') {
        const {event} = args;

        IntercomEventTracking.triggerIntercomEvent(event);
    } else {
        const {event, properties} = args;

        const timeToSend =
            event.name === TrackingEvents.BATCH_INVOICE_CREATE_AND_SEND_INVOICES_COMPLETE.name
                ? event.loadingTime
                : undefined;

        Famlytics.track(TrackingEvents[event.name], {
            ...properties,
            timeToSend,
        });
    }
};

const getTemporalPositionRelativeToInvoicePeriod = (from: string, to: string) => {
    const momentFrom = moment(from);
    const momentTo = moment(to);
    const now = moment();

    if (now.isAfter(momentTo, 'day')) {
        return 'Past';
    } else if (now.isBefore(momentFrom, 'day')) {
        return 'Future';
    } else {
        return 'Present';
    }
};

const mapIssueErrorCodeToTrackingCategory = (issueErrorCode: number) => {
    switch (issueErrorCode) {
        case 1518462380:
            return 'Bounced email';
        case 1505814389:
            return 'No plans or purchases';
        case 1490012406:
            return 'No bill payer for child';
        case 1505806357:
            return 'Missing email';
        default:
            return `Unknown ${issueErrorCode}`;
    }
};

export default combineEpics(listenForUpdates, fetchBatchInvoiceData, batchInvoice);
